/**
 * Sales order API surface (F6).
 *
 * Money arrives as strings, not numbers, and stays that way. The API sends `"1234.50"`
 * because a Postgres NUMERIC does not fit in a double, and parsing it here to render it
 * would reintroduce exactly the rounding the backend went to some trouble to avoid. The
 * only place a total becomes a number is `formatMoney`, at the last moment before a
 * human reads it.
 */

import type { Shipment } from '@/features/orders/ShipmentCard'
import { API_PREFIX, apiBlob, apiFetch, apiGet, type Envelope, type Page } from '@/api/client'
import type { ExportFormat } from '@/components/ExportButton'
import { discountPct, type ProductDiscount } from '@/features/inventory/api'

export const ORDER_STATUSES = [
  'draft',
  'submitted',
  'approved',
  'fulfilled',
  'invoiced',
  'completed',
  'cancelled',
] as const

export type OrderStatus = (typeof ORDER_STATUSES)[number]

/** What each status means on screen, and how it should look. */
export const STATUS_LABELS: Record<OrderStatus, string> = {
  draft: 'Draft',
  submitted: 'Awaiting approval',
  approved: 'Approved',
  fulfilled: 'Shipped',
  invoiced: 'Invoiced',
  completed: 'Completed',
  cancelled: 'Cancelled',
}

export interface OrderItem {
  id: string
  product_id: string
  quantity: number
  reserved_quantity: number
  unit_price: string
  discount_amount: string
  tax_amount: string
  line_total: string
  product_data: {
    code?: string
    name?: string
    list_price?: string
    unit_of_measure?: string
    tax_rate?: string
    /** PRD 4.1.3 -- set by the server at order time, never recomputed on screen. */
    price_anomaly?: boolean
    price_anomaly_reason?: string | null
  }
}

export interface OrderSummary {
  id: string
  order_number: string
  customer_id: string
  user_id: string
  location_id: string | null
  order_date: string
  delivery_date: string | null
  status: OrderStatus
  /** Where the order came from; see `ORDER_CHANNEL_LABELS`. */
  channel: OrderChannel
  subtotal: string
  discount_amount: string
  tax_amount: string
  /** The goods: lines less discount, plus tax. Delivery is on top of it. */
  total: string
  /** What the customer is charged for delivery. */
  shipping_amount: string
  /** What the customer owes for the order: `total` plus `shipping_amount`. */
  amount_due: string
  approved_at: string | null
  reservation_expires_at: string | null
  created_at: string
}

export interface OrderShippingAddress {
  name: string
  dial_code?: string
  phone?: string | null
  line1: string
  line2?: string | null
  city: string
  state?: string | null
  postal_code: string
  country: string
}

export interface Order extends OrderSummary, Shipment {
  /** Shiprocket's shipment id while a parcel is booked; staff only. */
  shipment_ref: string | null
  notes: string | null
  approved_by: string | null
  submitted_at: string | null
  fulfilled_at: string | null
  cancelled_at: string | null
  cancel_reason: string | null
  /** Only a storefront order moves off `unpaid`: it is paid by card at checkout. */
  payment_status: 'unpaid' | 'paid' | 'refunded'
  paid_at: string | null
  refunded_at: string | null
  updated_at: string
  /** Set on a marketplace order (the shopper's checkout address); null on one a rep raised. */
  shipping_address: OrderShippingAddress | null
  items: OrderItem[]
}

export interface CreditStatus {
  customer_id: string
  credit_limit: string | null
  outstanding: string
  available: string | null
  would_exceed: boolean
}

/**
 * `internal` is an order somebody in the business typed; `marketplace` is a shopper's own.
 * `pos` is not a real value of the `channel` column -- a till sale is a `pos_transactions`
 * row, not a `sales_orders` one, and the two are never confused in the database. It exists
 * only so this screen can tag the till sales it merges in for display; nothing sends it to
 * the `/orders` API as a filter (see `Orders.tsx`, which fetches the two sources apart and
 * never asks the server to filter on it).
 */
export type OrderChannel = 'internal' | 'marketplace' | 'pos'

export const ORDER_CHANNEL_LABELS: Record<OrderChannel, string> = {
  internal: 'Internal',
  marketplace: 'Marketplace',
  pos: 'POS',
}

export interface OrderFilters {
  page?: number
  q?: string
  status?: OrderStatus | ''
  channel?: OrderChannel | ''
  customer_id?: string
  location_id?: string
  date_from?: string
  date_to?: string
  awaiting_approval?: boolean
  /** Shipped storefront orders with no courier booked: never booked, or refused. */
  needs_courier?: boolean
}

export interface OrderLineInput {
  product_id: string
  quantity: number
  unit_price?: string
  discount_amount?: string
}

export interface OrderInput {
  customer_id: string
  location_id?: string | null
  delivery_date?: string | null
  notes?: string | null
  discount_amount?: string
  /** Left out, the server charges the ships-from location's rate; a figure overrides it. */
  shipping_amount?: string | null
  items?: OrderLineInput[]
}

function query(filters: OrderFilters): string {
  const params = new URLSearchParams()
  // Empty strings are "no filter", not "match empty" -- the select's placeholder option
  // and a cleared search box both land here.
  if (filters.page && filters.page > 1) params.set('page', String(filters.page))
  if (filters.q) params.set('q', filters.q)
  if (filters.status) params.set('status', filters.status)
  if (filters.channel) params.set('channel', filters.channel)
  if (filters.customer_id) params.set('customer_id', filters.customer_id)
  if (filters.location_id) params.set('location_id', filters.location_id)
  if (filters.date_from) params.set('date_from', filters.date_from)
  if (filters.date_to) params.set('date_to', filters.date_to)
  if (filters.awaiting_approval) params.set('awaiting_approval', 'true')
  if (filters.needs_courier) params.set('needs_courier', 'true')
  const qs = params.toString()
  return qs ? `?${qs}` : ''
}

export const exportOrders = (filters: OrderFilters, format: ExportFormat) => {
  const qs = query(filters)
  return apiBlob(`${API_PREFIX}/orders/export${qs ? `${qs}&fmt=${format}` : `?fmt=${format}`}`)
}

export const listOrders = (filters: OrderFilters = {}) =>
  apiFetch<Page<OrderSummary>>(`${API_PREFIX}/orders${query(filters)}`)

export const getOrder = (id: string) => apiGet<Order>(`${API_PREFIX}/orders/${id}`)

export const getCreditStatus = (customerId: string) =>
  apiGet<CreditStatus>(`${API_PREFIX}/orders/credit/${customerId}`)

// The caller owns the key's lifetime, not this function: a fresh UUID minted on every
// call would defeat the point, since a manual retry of the same order (the flaky-signal
// case platform.idempotency exists for) calls createOrder() again. Callers that care --
// the order wizard, mobile visit capture -- hold one key in a ref for the life of the
// attempt and pass it through; a caller that doesn't (a script, a test) still gets
// protection against the server retrying the write itself.
export const createOrder = (input: OrderInput, idempotencyKey = crypto.randomUUID()) =>
  apiGet<Order>(`${API_PREFIX}/orders`, {
    method: 'POST',
    headers: { 'Idempotency-Key': idempotencyKey },
    body: JSON.stringify(input),
  })

export const updateOrder = (id: string, input: Partial<OrderInput>) =>
  apiGet<Order>(`${API_PREFIX}/orders/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(input),
  })

export const replaceItems = (id: string, items: OrderLineInput[]) =>
  apiGet<Order>(`${API_PREFIX}/orders/${id}/items`, {
    method: 'PUT',
    body: JSON.stringify({ items }),
  })

/** `submit`, `approve`, `fulfil`, `cancel`, `reopen` -- all POSTs to a named sub-resource. */
const transition = (id: string, action: string, body?: unknown) =>
  apiGet<Order>(`${API_PREFIX}/orders/${id}/${action}`, {
    method: 'POST',
    body: JSON.stringify(body ?? {}),
  })

export const submitOrder = (id: string) => transition(id, 'submit')
export const reopenOrder = (id: string) => transition(id, 'reopen')
export const approveOrder = (id: string, notes?: string) => transition(id, 'approve', { notes })
export const fulfilOrder = (id: string) => transition(id, 'fulfil')
export const cancelOrder = (id: string, reason?: string) => transition(id, 'cancel', { reason })
/** Book the courier again once whatever refused the first booking is fixed. */
export const bookShipment = (id: string) => transition(id, 'book-shipment')
/** Call the booked parcel off; Shiprocket refuses once the courier has collected it. */
export const cancelShipment = (id: string) => transition(id, 'cancel-shipment')
/** A link to the parcel's label, or the pickup manifest, as a PDF Shiprocket made. */
export const shippingDocument = (id: string, kind: 'label' | 'manifest') =>
  apiGet<{ url: string }>(`${API_PREFIX}/orders/${id}/shipping-${kind}`)

/**
 * Cancel a paid storefront order before it ships, and send the money back to the card.
 *
 * Under `/marketplace` rather than `/orders` because the ERP's order module may not know
 * the storefront exists; the plain cancel above refuses a paid order with `ORDER_PAID`.
 */
export const refundOrder = (id: string, reason?: string) =>
  apiGet<Order>(`${API_PREFIX}/marketplace/orders/${id}/refund`, {
    method: 'POST',
    body: JSON.stringify({ reason }),
  })

export const deleteOrder = (id: string) =>
  apiFetch<void>(`${API_PREFIX}/orders/${id}`, { method: 'DELETE' })

// --- The two lookups the order screens need ------------------------------------------
// Customers and products have no feature folder of their own yet (their screens are M2).
// The order wizard needs to pick from both, so the two list calls live here rather than
// in a folder that exists only to hold them.

export interface CustomerOption {
  id: string
  code: string
  name: string
  tier: string
  credit_limit: string | null
  tax_exempt: boolean
  /**
   * The buyer's registration and where they are. Shown at the till because a sale to a
   * registered buyer is a B2B document -- the receipt has to name them and their GSTIN,
   * and the state is what decides CGST+SGST against IGST.
   */
  tax_id: string | null
  state: string | null
}

export interface ProductOption {
  id: string
  code: string
  name: string
  price: string
  unit_of_measure: string
  status: string
  discount_enabled?: boolean
  discounts?: ProductDiscount[] | null
}

/**
 * What the server will charge per unit on a sales order raised here on `day` (default
 * today): the list price less the biggest `sales_order` offer running then at the order's
 * location. See `discountPct`.
 */
export function orderUnitPrice(
  product: ProductOption,
  locationId: string | null,
  day?: string,
): string {
  const pct = discountPct(product, 'sales_order', locationId, day)
  if (pct <= 0) return product.price
  const cents = Math.round(Number(product.price) * 100)
  return (Math.round((cents * (100 - pct)) / 100) / 100).toFixed(2)
}

export const searchCustomers = (q = '') =>
  apiFetch<Page<CustomerOption>>(
    `${API_PREFIX}/customers?page_size=20${q ? `&q=${encodeURIComponent(q)}` : ''}`,
  )

export const searchProducts = (q = '') =>
  apiFetch<Page<ProductOption>>(
    `${API_PREFIX}/products?page_size=20&status=active${q ? `&q=${encodeURIComponent(q)}` : ''}`,
  )

export interface LocationOption {
  id: string
  code: string
  name: string
  type: string
}

export const listLocations = () =>
  apiFetch<Page<LocationOption>>(`${API_PREFIX}/locations?page_size=100`)

/**
 * Format a decimal-string amount for display.
 *
 * Takes the string the API sent and never sends the result back: once it has been through
 * `Number`, it is a rendering artefact, not a value.
 */
/** Rupees, en-IN grouping. Lives in `@/lib/money` so every screen prints one format. */
export { formatMoney } from '@/lib/money'

/** A response envelope is unwrapped by `apiGet`; this is only for the raw form. */
export type OrderEnvelope = Envelope<Order>
